<template>
  <div class="pp-tab-surface">
    <div
      v-if="hasInfoCells"
      class="pp-gi-grid"
    >
      <div
        v-if="pricePerArea"
        class="pp-gi-cell"
      >
        <span class="k">{{ t('property.priceUnitsTable.pricePerArea', { areaSuffix }) }}</span>
        <div class="v">
          <span class="chip range">{{ t('property.generalInfo.range') }}</span>
          <span class="val">{{ pricePerArea }}</span>
          <span class="cur">{{ currency }}</span>
        </div>
      </div>
      <div
        v-if="unitPriceRange"
        class="pp-gi-cell"
      >
        <span class="k">{{ t('property.v2Overview.unitPrice') }}</span>
        <div class="v">
          <span class="chip range">{{ t('property.generalInfo.range') }}</span>
          <span
            v-if="salesStatusLabel"
            class="chip sale"
            >{{ salesStatusLabel }}</span
          >
          <span class="val">{{ unitPriceRange }}</span>
          <span class="cur">{{ currency }}</span>
        </div>
      </div>
      <div
        v-if="serviceCharge"
        class="pp-gi-cell"
      >
        <span class="k">{{ t('property.v2Overview.serviceCharge') }}</span>
        <div class="v">
          <span class="chip area">{{ t('property.generalInfo.perArea', { areaSuffix }) }}</span>
          <span class="val">{{ serviceCharge }}</span>
          <span class="cur">{{ currency }}</span>
        </div>
      </div>
      <div
        v-if="propertyType"
        class="pp-gi-cell"
      >
        <span class="k">{{ t('property.v2Overview.propertyType') }}</span>
        <div class="v">
          <span class="chip offplan">{{ t('property.v2Overview.offPlanChip') }}</span>
          <span class="val">{{ propertyType }}</span>
        </div>
      </div>
      <div
        v-if="address"
        class="pp-gi-cell"
      >
        <span class="k">{{ t('property.generalInfo.location') }}</span>
        <div class="v">
          <button
            type="button"
            class="loc-link"
            @click="$emit('switch-tab', 'location')"
          >
            {{ address }}
            <span
              class="inline-block rtl:-scale-x-100"
              aria-hidden="true"
              >↗</span
            >
          </button>
        </div>
      </div>
      <div
        v-if="completionLabel"
        class="pp-gi-cell"
      >
        <span class="k">{{ t('property.v2Overview.handover') }}</span>
        <div class="v">
          <span class="val">{{ completionLabel }}</span>
          <span class="cur">{{ t('property.v2Overview.planned') }}</span>
        </div>
      </div>
    </div>

    <div
      v-if="hasDescription"
      class="pp-desc"
    >
      <div class="eyebrow">{{ t('property.v2Overview.aboutProject') }}</div>
      <h3>{{ title }}</h3>
      <div
        v-if="isHtmlDescription"
        class="prose"
        v-html="sanitizedDescriptionHtml"
      ></div>
      <p
        v-for="(para, idx) in plainDescriptionParagraphs"
        v-else
        :key="idx"
      >
        {{ para }}
      </p>

      <div
        v-if="hilites.length"
        class="hilites"
      >
        <div
          v-for="(hi, idx) in hilites"
          :key="idx"
          class="hi"
        >
          <span class="hi-l">{{ hi.label }}</span>
          <span class="hi-v">
            {{ hi.value }}
            <span
              v-if="hi.suffix"
              class="hi-suffix"
              >{{ hi.suffix }}</span
            >
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { PropertyDetailed, BedroomPrice, Apartment } from '@/types/properties';
import minBy from 'lodash/minBy';
import { htmlToSsrSafeHtml, sanitizeHtml } from '~/utils/sanitizeHtml';
import maxBy from 'lodash/maxBy';
import { convertCurrency } from '@/helpers/currency.helper';
import { M2_TO_FT2 } from '@/composables/useAreaFormat';
import {
  decodeHtmlEntities,
  fixDoubleEncodedHtmlEntities,
  normalizePropertyDescriptionText,
} from '@/helpers/propertyDescription';
import { SALE_STATUS_DISPLAY_MAP } from '@/constants/properties';
import { pickLocalized } from '@/utils/i18n/localizedField';
import { formatCompletionDate, formatPropertyType } from '@/helpers/properties';

const props = defineProps<{
  item: PropertyDetailed;
}>();

defineEmits<{
  (e: 'switch-tab', id: string): void;
}>();

const { t, locale } = useI18n();
const preferences = usePreferencesStore();
const { measureUnit } = storeToRefs(preferences);
const { areaSuffix } = useAreaFormat();

const aedFormatter = new Intl.NumberFormat('en-US', {
  style: 'decimal',
  notation: 'compact',
  maximumFractionDigits: 2,
});

const sourceCurrency = computed(() => props.item?.price?.currency || 'AED');
const currency = computed(() => preferences.currency);

const title = computed(() => pickLocalized(props.item?.title, locale.value));

const minPriceM2 = computed(() => {
  const min = minBy(props.item.br_prices, (item: BedroomPrice) => parseFloat(item.min_price_m2))?.min_price_m2;
  return Number(min) || 0;
});

const maxPriceM2 = computed(() => {
  const max = maxBy(props.item.br_prices, (item: BedroomPrice) => parseFloat(item.max_price_m2))?.max_price_m2;
  return Number(max) || 0;
});

const pricePerArea = computed(() => {
  if (!minPriceM2.value || !maxPriceM2.value) return null;
  const isFt2 = measureUnit.value === 'ft2';
  const divisor = isFt2 ? M2_TO_FT2 : 1;
  const min = convertCurrency(minPriceM2.value / divisor, sourceCurrency.value, currency.value);
  const max = convertCurrency(maxPriceM2.value / divisor, sourceCurrency.value, currency.value);
  return `${aedFormatter.format(min)} – ${aedFormatter.format(max)}`;
});

const unitPriceRange = computed(() => {
  const min = Number(props.item?.price?.min) || 0;
  const max = Number(props.item?.price?.max) || 0;
  if (!min || !max) return null;
  const minC = convertCurrency(min, sourceCurrency.value, currency.value);
  const maxC = convertCurrency(max, sourceCurrency.value, currency.value);
  return `${aedFormatter.format(minC)} – ${aedFormatter.format(maxC)}`;
});

const serviceCharge = computed(() => {
  const charge = (props.item as { service_charge?: unknown }).service_charge;
  let raw: string | number = 0;
  if (typeof charge === 'string') {
    try {
      const parsed = JSON.parse(charge);
      raw = parsed?.value || parsed || 0;
    } catch {
      raw = charge;
    }
  } else if (charge && typeof charge === 'object') {
    raw = (charge as { value?: number | string }).value ?? 0;
  } else if (typeof charge === 'number') {
    raw = charge;
  }
  let num = typeof raw === 'string' ? parseFloat(raw) || 0 : Number(raw) || 0;
  if (!num) return null;
  if (measureUnit.value === 'ft2') num /= M2_TO_FT2;
  return aedFormatter.format(convertCurrency(num, sourceCurrency.value, currency.value));
});

const propertyType = computed(() => formatPropertyType(props.item?.type));

const address = computed(() => props.item?.address);

const formatQuarterYear = (quarter: number, year: number) => t('property.shared.quarterYear', { quarter, year });

const completionLabel = computed(() =>
  props.item?.planned_completion_at ? formatCompletionDate(props.item.planned_completion_at, formatQuarterYear) : null,
);

const salesStatusLabel = computed(() => {
  const raw = props.item?.sales_status?.en;
  if (!raw) return null;
  return SALE_STATUS_DISPLAY_MAP[raw] || raw;
});

const hasInfoCells = computed(
  () =>
    !!(
      pricePerArea.value ||
      unitPriceRange.value ||
      serviceCharge.value ||
      propertyType.value ||
      address.value ||
      completionLabel.value
    ),
);

const rawDescription = computed(() => props.item?.description?.en?.trim() || '');
const isHtmlDescription = computed(() => /<[a-z][\s\S]*>/i.test(rawDescription.value));
const hasDescription = computed(() => rawDescription.value.length > 0);

// Stored descriptions are untrusted: sanitized with DOMPurify after mount (it needs a DOM); SSR and
// the first client render use the escaped plain text, so the copy stays in the server HTML for SEO.
const isMounted = ref(false);
onMounted(() => {
  isMounted.value = true;
});
const sanitizedDescriptionHtml = computed(() => {
  const html = fixDoubleEncodedHtmlEntities(rawDescription.value);
  return isMounted.value ? sanitizeHtml(html) : htmlToSsrSafeHtml(html);
});

const plainDescriptionParagraphs = computed(() => {
  if (isHtmlDescription.value) return [];
  const text = normalizePropertyDescriptionText(decodeHtmlEntities(rawDescription.value));
  return text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
});

type Hilite = { label: string; value: string; suffix?: string };

const hilites = computed<Hilite[]>(() => {
  const out: Hilite[] = [];
  const totalUnits = (props.item as { for_sale_count?: number | string }).for_sale_count;
  if (totalUnits) {
    out.push({ label: t('property.v2Overview.hilites.totalUnits'), value: String(totalUnits) });
  }
  const apartments = (props.item as { apartments?: Apartment[] }).apartments;
  if (apartments?.length) {
    out.push({ label: t('property.v2Overview.hilites.unitTypes'), value: String(apartments.length) });
  }
  const handoverLabel = formatCompletionDate(props.item?.planned_completion_at, formatQuarterYear);
  if (handoverLabel) {
    out.push({ label: t('property.v2Overview.handover'), value: handoverLabel });
  }
  return out;
});
</script>

<style scoped lang="scss">
.pp-tab-surface {
  background: #f8f7f3;
  border-radius: 20px;
  padding: 28px;
}

.pp-gi-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 24px;
}

.pp-gi-cell {
  padding: 16px 18px;
  background: white;
  border: 1px solid rgba(13, 15, 21, 0.08);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;

  .k {
    font-size: 13px;
    color: rgba(13, 15, 21, 0.62);
  }

  .v {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;

    .val {
      font-size: 14px;
      font-weight: 700;
      color: #0d0f15;
      font-variant-numeric: tabular-nums;
    }

    .cur {
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      font-size: 11px;
      color: rgba(13, 15, 21, 0.62);
      font-weight: 600;
    }

    .chip {
      padding: 2px 6px;
      border-radius: 5px;
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      font-size: 9px;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;

      &.range,
      &.area {
        background: #f3f3f3;
        color: rgba(13, 15, 21, 0.7);
      }

      &.offplan {
        background: #ffe9c8;
        color: #8b6000;
      }

      &.sale {
        background: rgba(57, 87, 211, 0.15);
        color: #3957d3;
      }
    }

    .loc-link {
      font-size: 14px;
      font-weight: 600;
      color: #0d0f15;
      text-decoration: underline;
      text-underline-offset: 3px;
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
    }
  }
}

.pp-desc {
  background: white;
  border: 1px solid rgba(13, 15, 21, 0.08);
  border-radius: 16px;
  padding: 24px 26px;

  .eyebrow {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 10px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: rgba(13, 15, 21, 0.62);
    font-weight: 700;
    margin-bottom: 10px;
  }

  h3 {
    font-family: 'Fraunces', 'Times New Roman', serif;
    font-weight: 600;
    font-size: 24px;
    letter-spacing: -0.015em;
    margin: 0 0 12px;
    line-height: 1.15;
  }

  p {
    font-size: 14px;
    line-height: 1.6;
    color: rgba(13, 15, 21, 0.7);
    margin-bottom: 10px;
  }

  .prose {
    font-size: 14px;
    line-height: 1.6;
    color: rgba(13, 15, 21, 0.7);

    :deep(p) {
      margin-bottom: 10px;
    }

    :deep(strong) {
      font-weight: 600;
    }
  }

  .hilites {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    margin-top: 18px;
    padding-top: 18px;
    border-top: 1px solid rgba(13, 15, 21, 0.08);
  }

  .hi {
    display: flex;
    flex-direction: column;
    gap: 4px;

    .hi-l {
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      font-size: 9px;
      letter-spacing: 0.1em;
      color: rgba(13, 15, 21, 0.62);
      font-weight: 600;
      text-transform: uppercase;
    }

    .hi-v {
      font-family: 'Fraunces', 'Times New Roman', serif;
      font-weight: 600;
      font-size: 20px;
      letter-spacing: -0.02em;
      line-height: 1.1;
    }

    .hi-suffix {
      font-size: 12px;
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      color: rgba(13, 15, 21, 0.62);
      font-weight: 500;
    }
  }
}
</style>
